import React, {useState} from 'react';
import {Modal} from '@/components/common-components/components/modal';
import {CommonInput} from '@/components/common-components/components';
import {Button as V2Button, GrayButton} from '@/components/common-components/v2/Button';
import {Select, notification, Divider} from 'antd';
import {CaretDownFilled} from '@ant-design/icons';
import {observer} from 'mobx-react-lite';


// Stylesheets
import styles from './style.module.scss';
import {useStore} from '@/store/root-store';
import {User} from './user';
import {useTranslation} from 'next-i18next';
import {SCA_PAGE_SHARED} from '@/constants/events';
import styled from 'styled-components';

import {MixPanel} from '@/utils/mixpanel';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faXmark} from '@fortawesome/pro-regular-svg-icons';
import {emailReg} from '@/constants/regex';
const {Option} = Select;

const ShareModal: React.FC = observer(() => {
  const {
    contentOptimizer: {
      currentPage: {content, shareDialog, pagePermissions},
    },
  } = useStore('');
  const [email, setEmail] = useState<string>('');
  const [permission, setPermission] = useState<'edit'>('edit');
  const [globalPermission, setGlobalPermission] = useState<'edit' | 'view'>(pagePermissions.isEditableByAnyone ? 'edit' : 'view');
  const {t} = useTranslation('common');

  const handlePermissionChange = (value: 'edit') => {
    setPermission(value);
  };

  const handleGlobalPermissionsUpdate = async (value: 'view' | 'edit') => {
    const existingPermission = pagePermissions?.isEditableByAnyone ? 'edit' : 'view';
    const existingViewPermission = pagePermissions?.isViewableByAnyone;

    const payload = {
      isEditableByAnyone: value == 'edit',
      isViewableByAnyone: value == 'view',
    };

    navigator.clipboard.writeText(location?.href?.replace('seo-content-assistant', 'seo-content-assistant-public'));

    // only call for updating global page permission if option has been change from default
    if ((!existingPermission && value) || !existingViewPermission || (existingPermission !== value)) {
      await pagePermissions.editPagePermissions(payload);
    } else {
      notification.success({message: 'Public page URL is copied to clipboard.'});
    }
    // if existing global page permission is not changed, show copy notification
    shareDialog.closeDialog();
    setEmail('');

    MixPanel.track(SCA_PAGE_SHARED, {
      'pageId': content.uuid,
      'description': 'Page was shared',
    });
  };

  const handleInviteUser = (email, permission, closeAfterSubmit = true) => {
    if (email) {
      pagePermissions.editPagePermissions({emails: [email], permission});
      if (closeAfterSubmit) shareDialog.closeDialog();
      setEmail('');

      MixPanel.track(SCA_PAGE_SHARED, {
        'pageId': content.uuid,
        'description': 'Page was shared',
      });
    } else {
      notification.warn({message: t('looks-like-forgot')});
    }
  };

  return (
    <ModalStyled visible={shareDialog.isOpen}
      closable={true}
      onClose={() => shareDialog.closeDialog()}
      width={400}
      destroyOnClose={true}
      closeIcon={<FontAwesomeIcon icon={faXmark} color='#fff' />}
      bodyStyle={{padding: '27px 20px'}}>
      <Container>
        <Title>PEOPLE WITH ACCESS</Title>
        <Head>
          <InputContainer>
            <CommonInput onChange={e => setEmail(e?.target?.value)}
              className={styles.customInput}
              variant='transparent'
              placeholder='Email address'/>
            <Select
              value={permission}
              onChange={handlePermissionChange}
              suffixIcon={<CaretDownFilled style={{color: '#121212'}}/>}
              className={styles.customSelect}>
              <Option value='view'>{t('can-view')}</Option>
              <Option value='edit'>{t('can-edit')}</Option>
            </Select>
          </InputContainer>
          <V2Button color='neonBlue'
            disabled={!email?.length || !email?.match(emailReg)}
            style={{padding: '4px 15px', marginRight: 0}}
            onClick={() => handleInviteUser(email, permission)}>Send</V2Button>
        </Head>
        <div style={{height: 22}}>
          {(email?.length && email?.match(emailReg)) ? null : !email?.length ? null : <EmailError>Please enter a valid email address</EmailError>}
        </div>

        <Body>
          <ul className={styles.users}>
            <li>
              <User avatar={content.createdByUser?.slice(0, 1) ?? 'U'} email={content.createdByUser} isOwner={true}/>
            </li>
            {
              pagePermissions.users?.sort().map(user => {
                return (
                  <li key={user.email}>
                    <User avatar={user.email.slice(0, 1) ?? 'U'} email={user.email} permission={(user.permission).toLowerCase()} onPermissionChange={handleInviteUser}/>
                  </li>
                );
              })
            }
          </ul>
        </Body>
        <Divider />
        <Title>GENERAL SETTINGS</Title>
        <Footer>
          <Select
            value={globalPermission}
            onChange={value => setGlobalPermission(value)}
            suffixIcon={<CaretDownFilled style={{color: '#121212'}}/>}
            className={styles.customGeneralSelect}>
            <Option value='view'>{'Anyone with the link can view'}</Option>
            <Option value='edit'>{'Anyone with the link can edit'}</Option>
          </Select>
          <GrayButton style={{paddingBottom: 4, paddingTop: 4, backgroundColor: '#F9F9FB'}}
            color='gray'
            onClick={() => handleGlobalPermissionsUpdate(globalPermission)}
          >
                      Copy link
          </GrayButton>
        </Footer>


      </Container>
    </ModalStyled>
  );
});

export default ShareModal;

const ModalStyled = styled(Modal)`
margin-top: auto;
margin-bottom: auto;

.rc-dialog-body {
  padding: 27px 20px !important;


}

.rc-dialog-close {
  right: -30px !important;
  opacity: .5;

  svg {
    color: #fff;
  }
}

`;
const Container = styled.div`

`;
const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #121212;
  margin-bottom: 10px;
`;
const Head = styled.div`
  display: flex;

`;
const Body = styled.div`
  display: flex;
  min-height: 250px;

`;
const Footer = styled.div`
  display: flex;
  justify-content: space-between;

`;
const InputContainer = styled.div`
  border: 1px solid #E8E8E8;
  border-radius: 5px;
  background-color: #f9f9f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 32px;
`;
const EmailError = styled.div`
  color: #ff5959;
  font-size: 12px;
  font-weight: 400;
`;


